import styled from 'styled-components';
import { InputNumber, Button, Radio, Collapse } from 'antd';

// Main container - no card styling, ThemeMainWrapper already provides the card appearance
export const FormContainer = styled.div`
  width: 100%;
  position: relative;
`;

// Header section at the top inside ThemeMainWrapper
export const HeaderSection = styled.div`
  background: ${({ theme }) => theme.settings.whiteColor};
  border-bottom: 1px solid ${({ theme }) => theme.settings.borderColor};
  border-radius: 12px 12px 0 0;
  padding: 10px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height:60px;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const BackButton = styled(Button)`
  background: #ffffff !important;
  border: 1px solid rgba(61, 41, 105, 0.2) !important;
  border-radius: 12px !important;
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 !important;
  box-shadow: none !important;

  &:hover {
    background: #f5f5f5 !important;
    border-color: rgba(61, 41, 105, 0.3) !important;
  }

  svg {
    font-size: 16px;
    color: #0a1b39;
  }
`;

export const FormTitle = styled.h1`
  font-family: ${({ theme }) => theme.fonts.inter};
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.settings.primary};
  margin: 0;
`;

// Form content wrapper with proper spacing (similar to ContentWrapper in CampaignDetail)
export const FormContent = styled.div`
  padding: 32px 24px;

  /* Form item spacing */
  .ant-form-item {
    margin-bottom: 16px !important;
  }

  /* Form field label styling matching Figma */
  .ant-form-item-label {
    padding-bottom: 6px !important;
  }

  .ant-form-item-label > label {
    font-family: 'Poppins', sans-serif !important;
    font-weight: 500 !important;
    font-size: 12px !important;
    line-height: 22px !important;
    color: #485066 !important;
    height: auto !important;
    width: 100% !important;
    display: flex !important;
  }

  .ant-form-item-label > label::after {
    display: none !important;
  }

  /* Input field styling matching Figma */
  .ant-input,
  .ant-select-selector,
  .ant-input-number {
    border: 1px solid rgba(10, 27, 57, 0.1) !important;
    border-radius: 10px !important;
    height: 48px !important;
    font-family: 'Poppins', sans-serif !important;
    font-size: 14px !important;
    color: #0a1b39 !important;
  }

  .ant-input:hover,
  .ant-select-selector:hover,
  .ant-input-number:hover {
    border-color: rgba(10, 27, 57, 0.2) !important;
  }

  .ant-input:focus,
  .ant-input-focused,
  .ant-select-focused .ant-select-selector,
  .ant-input-number-focused {
    border-color: #3d2969 !important;
    box-shadow: none !important;
  }

  /* Input number specific */
  .ant-input-number-input {
    font-family: 'Poppins', sans-serif !important;
    font-size: 14px !important;
    height: 46px !important;
  }

  /* Select dropdown styling */
  .ant-select-selection-placeholder {
    font-family: 'Poppins', sans-serif !important;
    font-size: 14px !important;
    color: rgba(10, 27, 57, 0.5) !important;
  }

  .ant-select-selection-item {
    font-family: 'Poppins', sans-serif !important;
    font-size: 14px !important;
    color: #0a1b39 !important;
    line-height: 46px !important;
  }

  /* Tag styling for multi-select */
  .ant-select-multiple .ant-select-selection-item {
    background: rgba(38, 14, 82, 0.1) !important;
    border: none !important;
    border-radius: 4px !important;
    padding: 4px 8px !important;
    line-height: 22px !important;
    height: auto !important;
    font-family: 'Poppins', sans-serif !important;
    font-size: 14px !important;
    color: #0a1b39 !important;
  }

  .ant-select-selection-item-remove {
    color: #0a1b39 !important;
  }

  /* Arrow icon for select */
  .ant-select-arrow {
    color: #0a1b39 !important;
  }

  /* Multiple select padding adjustment */
  .ant-select-multiple .ant-select-selector {
    padding: 4px 11px !important;
    min-height: 48px !important;
    height: auto !important;
  }

  /* Disabled state */
  .ant-input:disabled,
  .ant-select-disabled .ant-select-selector,
  .ant-input-number-disabled {
    background: #f5f5f5 !important;
    color: rgba(10, 27, 57, 0.5) !important;
  }

  /* TinyMCE Editor styling */
  .tox-tinymce {
    border: 1px solid rgba(10, 27, 57, 0.1) !important;
    border-radius: 10px !important;
  }

  .tox .tox-toolbar {
    background: #ffffff !important;
    border-bottom: 1px solid rgba(10, 27, 57, 0.1) !important;
  }

  .tox .tox-edit-area {
    border: none !important;
  }

  .tox .tox-edit-area__iframe {
    background: #ffffff !important;
  }
`;

// Action buttons wrapper at the bottom
export const ActionButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

// Update Campaign button
export const UpdateButton = styled(Button)`
  background: #3d2969 !important;
  border: 1px solid #3d2969 !important;
  border-radius: 10px !important;
  height: 42px !important;
  padding: 0 16px !important;
  min-width: 160px !important;

  &:hover:not(:disabled) {
    background: #2a1b4b !important;
    border-color: #2a1b4b !important;
  }

  &:disabled {
    background: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: #ffffff !important;
  }

  span {
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    color: #ffffff;
  }
`;

// Cancel button
export const CancelButton = styled(Button)`
  background: #ffffff !important;
  border: 1px solid rgba(61, 41, 105, 0.2) !important;
  border-radius: 10px !important;
  height: 42px !important;
  padding: 0 16px !important;

  &:hover {
    background: #f5f5f5 !important;
    border-color: rgba(61, 41, 105, 0.3) !important;
  }

  span {
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    color: #3d2969;
  }
`;

// Styled input number matching Figma
export const StyledInputNumber = styled(InputNumber)`
  width: 100%;
  border-radius: 10px;
  height: 48px;

  .ant-input-number-input {
    font-family: 'Poppins', sans-serif;
    font-size: 14px;
    color: #0a1b39;
    height: 46px;
    padding: 0 16px;
  }
`;

// Inline Radio Group matching Survey Builder style
export const InlineRadioGroup = styled.div`
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 0 20px;
  border: 1px solid rgba(10, 27, 57, 0.1);
  border-radius: 10px;
  background: ${({ theme }) => theme.settings.whiteColor};

  .ant-radio-group {
    display: flex;
    align-items: center;
    gap: 32px;
  }

  .ant-radio-wrapper {
    font-size: 14px;
    font-family: 'Poppins', sans-serif;
    color: #1f2430;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;

    .ant-radio {
      top: 0;
    }

    .ant-radio-inner {
      width: 14px;
      height: 14px;
      border: 1px solid rgba(31, 36, 48, 0.2);
    }

    .ant-radio-checked .ant-radio-inner {
      border-color: #3d2969 !important;
      background-color: #3d2969 !important;
    }

    .ant-radio:hover .ant-radio-inner {
      border-color: #3d2969 !important;
    }

    .ant-radio-inner::after {
      background-color: white !important;
      width: 6px;
      height: 6px;
      margin-block-start: -3px;
      margin-inline-start: -3px;
    }
  }
`;

// Age input wrapper with grey Min/Max labels matching Figma
export const AgeInputWrapper = styled.div`
  display: flex;
  border: 1px solid rgba(10, 27, 57, 0.1);
  border-radius: 10px;
  height: 48px;
  overflow: hidden;
  background: white;

  &:hover {
    border-color: rgba(10, 27, 57, 0.2);
  }

  &:focus-within {
    border-color: #3d2969;
  }

  .age-label {
    background: #eae7ee;
    border-right: 1px solid rgba(10, 27, 57, 0.1);
    padding: 0 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: #485066;
    min-width: 56px;
    flex-shrink: 0;
  }

  .ant-input-number {
    flex: 1;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    height: 46px !important;

    &:hover,
    &:focus,
    &.ant-input-number-focused {
      border: none !important;
      box-shadow: none !important;
    }

    .ant-input-number-input {
      height: 46px;
      border: none;
      box-shadow: none;
    }
  }
`;

// Income input wrapper with $ prefix (similar to AgeInputWrapper but with spinners)
export const IncomeInputWrapper = styled.div`
  display: flex;
  border: 1px solid rgba(10, 27, 57, 0.1);
  border-radius: 10px;
  height: 48px;
  overflow: hidden;
  background: white;
  flex: 1;

  &:hover {
    border-color: rgba(10, 27, 57, 0.2);
  }

  &:focus-within {
    border-color: #3d2969;
  }

  .income-prefix {
    background: #eae7ee;
    border-right: 1px solid rgba(10, 27, 57, 0.1);
    padding: 0 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Poppins', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #485066;
    flex-shrink: 0;
  }

  .ant-input-number {
    flex: 1;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    height: 46px !important;

    &:hover,
    &:focus,
    &.ant-input-number-focused {
      border: none !important;
      box-shadow: none !important;
    }

    .ant-input-number-input {
      height: 46px;
      border: none;
      box-shadow: none;
      font-family: 'Poppins', sans-serif;
      font-size: 14px;
      color: #0a1b39;
    }

    .ant-input-number-handler-wrap {
      display: none;
    }
  }
`;

// Age Range Slider Components (matching Survey Builder)
export const AgeRangeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  .fieldLabel {
    font-size: 12px;
    font-weight: 500;
    color: #485066;
    font-family: 'Poppins', sans-serif;
  }

  .ageValue {
    font-size: 12px;
    font-weight: 600;
    color: #3d2969;
    font-family: 'Poppins', sans-serif;
    background: rgba(61, 41, 105, 0.1);
    padding: 2px 8px;
    border-radius: 4px;
  }
`;

export const AgeSliderWrapper = styled.div`
  padding: 4px 0 0 0;
  position: relative;

  .ant-slider {
    margin: 0;
    height: 14px;
  }

  .ant-slider-rail {
    height: 6px;
    background: #f0f0f0;
    border-radius: 100px;
    top: 4px;
  }

  .ant-slider-track {
    height: 6px;
    background: linear-gradient(90deg, #a8a0b8 0%, #3d2969 100%);
    border-radius: 100px;
    top: 4px;
  }

  .ant-slider-handle {
    width: 14px !important;
    height: 14px !important;
    border: 2px solid #3d2969 !important;
    background: #3d2969 !important;
    border-radius: 50% !important;
    box-shadow: none !important;
    margin-top: 0 !important;
    top: 0 !important;
    transform: translateX(-50%) !important;

    &::before,
    &::after {
      display: none !important;
    }

    &:hover,
    &:focus,
    &:active {
      background: #3d2969 !important;
      border-color: #3d2969 !important;
      box-shadow: 0 0 0 5px rgba(61, 41, 105, 0.12) !important;
    }
  }

  .ant-slider-mark {
    display: none;
  }

  .ant-slider-dot {
    display: none;
  }

  .ant-tooltip {
    .ant-tooltip-inner {
      background: #1f2430;
      font-size: 12px;
      font-weight: 500;
      min-height: 24px;
      padding: 4px 8px;
      border-radius: 4px;
    }
    .ant-tooltip-arrow::before {
      background: #1f2430;
    }
  }
`;

export const TickMarksRuler = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 8px;
  padding: 0 5px;
`;

export const TickMark = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;

  &::before {
    content: '';
    width: 1px;
    height: ${({ $isMajor }) => ($isMajor ? '8px' : '5px')};
    background: ${({ $isMajor }) => ($isMajor ? 'rgba(31, 36, 48, 0.25)' : 'rgba(31, 36, 48, 0.12)')};
  }

  .label {
    font-size: 10px;
    font-weight: 500;
    color: rgba(31, 36, 48, 0.6);
    font-family: 'Poppins', sans-serif;
    margin-top: 2px;
    white-space: nowrap;
  }
`;

// Study Type Radio Group - Card style matching input height
export const StudyTypeRadioGroup = styled(Radio.Group)`
  width: 100%;
  position: relative;
  display: flex !important;
  flex-direction: row;
  gap: 12px;

  .ant-radio-wrapper {
    flex: 1;
    margin: 0;
    padding: 0 16px;
    border: 1px solid rgba(10, 27, 57, 0.1);
    border-radius: 10px;
    background: ${({ theme }) => theme.settings.whiteColor || '#ffffff'};
    display: flex !important;
    align-items: center !important;
    justify-content: space-between;
    cursor: pointer;
    transition: all 0.2s ease;
    height: 48px;

    /* When only one option exists, don't stretch to full width */
    &:only-child {
      flex: 0 1 auto;
    }

    &:hover:not(.ant-radio-wrapper-disabled) {
      border-color: rgba(10, 27, 57, 0.2);
    }

    .ant-radio {
      display: none;
    }

    .ant-radio + span {
      flex: 1;
      padding: 0;
      display: flex !important;
      align-items: center !important;
      justify-content: space-between;
      width: 100%;
      min-height: 0;
    }

    .radio-label-content {
      display: flex !important;
      flex-direction: column;
      gap: 0;
      flex: 1;
      min-height: 0;
      justify-content: center;
      align-items: flex-start;
      padding: 0;

      .radio-title {
        font-family: 'Poppins', sans-serif;
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
        color: #0a1b39;
        transition: color 0.2s ease;
        margin: 0;
      }

      .radio-subtitle {
        font-family: 'Poppins', sans-serif;
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
        color: ${({ theme }) => theme.settings.textColor || '#485066'};
        transition: color 0.2s ease;
        margin: 0;
      }
    }

    .radio-icon {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      border: 1px solid rgba(10, 27, 57, 0.2);
      display: flex !important;
      align-items: center !important;
      justify-content: center;
      flex-shrink: 0;
      margin-left: 12px;
      transition: all 0.2s ease;

      img {
        display: none;
        width: 10px;
        height: 10px;
      }
    }

    &.ant-radio-wrapper-checked {
      border-color: #3d2969;
      background: rgba(61, 41, 105, 0.02);

      .radio-label-content {
        .radio-title {
          color: #3d2969;
        }

        .radio-subtitle {
          color: #3d2969;
        }
      }

      .radio-icon {
        border-color: #3d2969;
        background-color: #3d2969;

        img {
          display: block;
          filter: brightness(0) invert(1);
        }
      }
    }

    &:not(.ant-radio-wrapper-checked) {
      .radio-label-content {
        .radio-title {
          color: #0a1b39;
        }

        .radio-subtitle {
          color: #485066;
        }
      }
    }

    &.ant-radio-wrapper-disabled {
      opacity: 0.6;
      cursor: not-allowed;
      border-style: dashed;
      border-color: rgba(10, 27, 57, 0.1);
      background: ${({ theme }) => theme.settings.backgroundSecondary || '#fafafa'};

      &:hover {
        border-color: rgba(10, 27, 57, 0.1);
      }

      .radio-label-content {
        .radio-title {
          color: #9ca3af;
        }

        .radio-subtitle {
          color: #9ca3af;
        }
      }

      .radio-icon {
        opacity: 0.5;
      }
    }
  }
`;

// ============================================
// Advanced Targeting Styles
// ============================================

export const AdvancedTargetingWrapper = styled.div`
  margin-top: 24px;
  border: 1px solid rgba(10, 27, 57, 0.1);
  border-radius: 10px;
  background: #ffffff;
  overflow: hidden;
`;

export const AdvancedTargetingCollapse = styled(Collapse)`
  background: transparent !important;
  border: none !important;

  .ant-collapse-item {
    border: none !important;
    background: transparent !important;
  }

  .ant-collapse-header {
    padding: 16px 20px !important;
    align-items: center !important;
    background: #f8f7fa !important;
    border-bottom: 1px solid rgba(10, 27, 57, 0.05);

    &:hover {
      background: #f0eef4 !important;
    }
  }

  .ant-collapse-content {
    border: none !important;
    background: #ffffff !important;
  }

  .ant-collapse-content-box {
    padding: 20px !important;
  }

  .ant-collapse-expand-icon {
    color: #3d2969 !important;
    font-size: 12px !important;
  }

  .ant-collapse-item-active {
    .ant-collapse-header {
      border-bottom: 1px solid rgba(10, 27, 57, 0.1);
    }
  }
`;

export const AdvancedTargetingHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  .header-title {
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    font-size: 14px;
    color: #0a1b39;
  }

  .header-count {
    background: #3d2969;
    color: #ffffff;
    font-family: 'Poppins', sans-serif;
    font-size: 11px;
    font-weight: 500;
    padding: 2px 8px;
    border-radius: 10px;
    min-width: 20px;
    text-align: center;
  }

  .header-subtitle {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    color: #485066;
    margin-left: auto;
  }
`;

export const AdvancedTargetingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

export const AddDefinitionBox = styled.div`
  background: #f8f7fa;
  border: 2px dashed rgba(61, 41, 105, 0.2);
  border-radius: 10px;
  min-height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3d2969;
    background: rgba(61, 41, 105, 0.05);
  }

  .add-text {
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: #3d2969;
  }
`;

export const DefinitionCard = styled.div`
  background: #ffffff;
  border: 1px solid rgba(10, 27, 57, 0.1);
  border-radius: 10px;
  padding: 14px;
  position: relative;
  min-height: 90px;
  display: flex;
  flex-direction: column;

  .definition-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 10px;
    gap: 8px;
  }

  .definition-title {
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #0a1b39;
    flex: 1;
    line-height: 1.3;
  }

  .definition-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .action-icon {
    font-size: 14px;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: all 0.15s ease;

    &.edit-icon {
      color: #3d2969;

      &:hover {
        background: rgba(61, 41, 105, 0.1);
      }
    }

    &.delete-icon {
      color: #ff4d4f;

      &:hover {
        background: rgba(255, 77, 79, 0.1);
      }
    }
  }

  .definition-selections {
    flex: 1;
  }

  .selection-tag {
    display: inline-block;
    background: rgba(61, 41, 105, 0.1);
    color: #3d2969;
    font-family: 'Poppins', sans-serif;
    font-size: 11px;
    padding: 3px 8px;
    border-radius: 4px;
    margin: 2px 4px 2px 0;
  }

  .selection-more {
    display: inline-block;
    color: #485066;
    font-family: 'Poppins', sans-serif;
    font-size: 11px;
    padding: 3px 0;
  }

  .definition-quota {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(10, 27, 57, 0.06);
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .at-quota-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: rgba(61, 41, 105, 0.1);
    padding: 2px 8px;
    border-radius: 4px;
    font-family: 'Poppins', sans-serif;
    font-size: 11px;
    font-weight: 600;
    color: #3d2969;

    .at-quota-icon {
      font-size: 11px;
      cursor: pointer;
      padding: 1px;
      border-radius: 2px;
      transition: all 0.15s ease;

      &.edit-icon {
        color: #3d2969;
        &:hover { background: rgba(61, 41, 105, 0.15); }
      }
      &.delete-icon {
        color: #ff4d4f;
        &:hover { background: rgba(255, 77, 79, 0.15); }
      }
    }
  }

  .at-add-quota-btn {
    font-family: 'Poppins', sans-serif;
    font-size: 11px;
    font-weight: 600;
    color: #3d2969;
    background: rgba(61, 41, 105, 0.08);
    border: none;
    padding: 2px 8px;
    border-radius: 4px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
      background: rgba(61, 41, 105, 0.15);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
`;

// Modal Styles
export const ATModalContent = styled.div`
  .modal-search {
    margin-bottom: 20px;
  }

  .question-text {
    font-family: 'Poppins', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #0a1b39;
    margin-bottom: 16px;
    padding: 12px 16px;
    background: #f8f7fa;
    border-radius: 8px;
  }

  .options-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .options-count {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    color: #485066;
  }

  .options-actions {
    display: flex;
    gap: 12px;
  }

  .action-btn {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: #3d2969;
    cursor: pointer;
    background: none;
    border: none;
    padding: 4px 12px;
    border-radius: 4px;
    transition: background 0.2s ease;

    &:hover {
      background: rgba(61, 41, 105, 0.1);
    }

    &.clear-btn {
      color: #ff4d4f;

      &:hover {
        background: rgba(255, 77, 79, 0.1);
      }
    }
  }

  .options-list {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid rgba(10, 27, 57, 0.1);
    border-radius: 8px;
  }

  .option-item {
    padding: 10px 16px;
    border-bottom: 1px solid rgba(10, 27, 57, 0.05);
    cursor: pointer;
    transition: background 0.15s ease;
    display: flex;
    align-items: center;
    gap: 10px;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: rgba(61, 41, 105, 0.03);
    }

    &.selected {
      background: rgba(61, 41, 105, 0.08);
    }

    .option-checkbox {
      width: 16px;
      height: 16px;
      border: 1px solid rgba(10, 27, 57, 0.2);
      border-radius: 3px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      &.checked {
        background: #3d2969;
        border-color: #3d2969;

        svg {
          color: white;
          font-size: 10px;
        }
      }
    }

    .option-label {
      font-family: 'Poppins', sans-serif;
      font-size: 13px;
      color: #0a1b39;
      flex: 1;
    }
  }

  .selected-tags {
    margin-top: 16px;
    padding: 12px;
    background: #f8f7fa;
    border-radius: 8px;
    min-height: 60px;

    .selected-tags-title {
      font-family: 'Poppins', sans-serif;
      font-size: 12px;
      font-weight: 500;
      color: #485066;
      margin-bottom: 8px;
    }

    .selected-tag {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: white;
      border: 1px solid rgba(61, 41, 105, 0.2);
      color: #3d2969;
      font-family: 'Poppins', sans-serif;
      font-size: 12px;
      padding: 4px 10px;
      border-radius: 4px;
      margin: 3px 6px 3px 0;

      .remove-tag {
        cursor: pointer;
        font-size: 10px;
        opacity: 0.7;

        &:hover {
          opacity: 1;
        }
      }
    }
  }
`;

export const QuestionSearchWrapper = styled.div`
  position: relative;

  .search-input {
    width: 100%;
    height: 48px;
    border: 1px solid rgba(10, 27, 57, 0.1);
    border-radius: 10px;
    padding: 0 16px 0 44px;
    font-family: 'Poppins', sans-serif;
    font-size: 14px;
    color: #0a1b39;
    outline: none;
    transition: border-color 0.2s ease;

    &:hover {
      border-color: rgba(10, 27, 57, 0.2);
    }

    &:focus {
      border-color: #3d2969;
    }

    &::placeholder {
      color: rgba(10, 27, 57, 0.4);
    }
  }

  .search-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(10, 27, 57, 0.4);
    font-size: 16px;
  }

  .browse-btn {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: #3d2969;
    cursor: pointer;
    background: rgba(61, 41, 105, 0.1);
    border: none;
    padding: 6px 12px;
    border-radius: 6px;
    transition: background 0.2s ease;

    &:hover {
      background: rgba(61, 41, 105, 0.15);
    }
  }
`;

export const QuestionDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid rgba(10, 27, 57, 0.1);
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  margin-top: 4px;

  .dropdown-item {
    padding: 12px 16px;
    cursor: pointer;
    border-bottom: 1px solid rgba(10, 27, 57, 0.05);
    transition: background 0.15s ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: rgba(61, 41, 105, 0.05);
    }

    &.selected {
      background: rgba(61, 41, 105, 0.1);
    }

    .item-name {
      font-family: 'Poppins', sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: #0a1b39;
    }

    .item-text {
      font-family: 'Poppins', sans-serif;
      font-size: 12px;
      color: #485066;
      margin-top: 4px;
    }
  }

  .no-results {
    padding: 20px 16px;
    text-align: center;
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    color: #485066;
  }

  .loading {
    padding: 20px 16px;
    text-align: center;
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    color: #485066;
  }
`;

// ============================================
// Quota Styles
// ============================================

export const FieldLabelWithQuota = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;

  .label-required {
    color: #ff4d4f;
  }

  .add-quota-btn {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: #3d2969;
    background: rgba(61, 41, 105, 0.1);
    border: none;
    padding: 2px 8px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s ease;
    display: inline-flex;
    align-items: center;
    gap: 3px;

    .anticon {
      font-size: 10px;
    }

    &:hover:not(:disabled) {
      background: rgba(61, 41, 105, 0.18);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .census-btn {
    margin-left: auto;
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: #3d2969;
    background: rgba(61, 41, 105, 0.1);
    border: none;
    padding: 2px 8px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s ease;
    display: inline-flex;
    align-items: center;

    &:hover:not(:disabled) {
      background: rgba(61, 41, 105, 0.18);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .quota-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: rgba(61, 41, 105, 0.1);
    padding: 2px 8px;
    border-radius: 4px;

    .quota-text {
      font-family: 'Poppins', sans-serif;
      font-size: 12px;
      font-weight: 600;
      color: #3d2969;
    }

    .quota-icon {
      font-size: 12px;
      cursor: pointer;
      padding: 1px;
      border-radius: 2px;
      transition: all 0.15s ease;

      &.edit-icon {
        color: #3d2969;
        &:hover {
          background: rgba(61, 41, 105, 0.15);
        }
      }

      &.delete-icon {
        color: #ff4d4f;
        &:hover {
          background: rgba(255, 77, 79, 0.15);
        }
      }
    }
  }
`;

export const QuotaModalContent = styled.div`
  .quota-warning {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    color: #485066;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(10, 27, 57, 0.08);
  }

  .quota-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 0 10px 0;
    border-bottom: 1px solid rgba(10, 27, 57, 0.1);
    margin-bottom: 12px;

    .quota-label,
    .quota-count-label {
      font-family: 'Poppins', sans-serif;
      font-size: 11px;
      font-weight: 600;
      color: #485066;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .quota-count-label {
      width: 100px;
      text-align: right;
    }
  }

  .quota-search-input {
    margin-bottom: 12px;
    border-radius: 8px !important;
    height: 36px !important;

    .ant-input {
      font-family: 'Poppins', sans-serif !important;
      font-size: 12px !important;
      height: auto !important;
      border: none !important;
    }

    .ant-input-prefix {
      color: #485066;
      font-size: 12px;
    }
  }

  .quota-entries {
    max-height: 300px;
    overflow-y: auto;
    margin-bottom: 16px;
  }

  .quota-error {
    font-family: 'Poppins', sans-serif;
    font-size: 11px;
    color: #cf1322;
    margin-top: 8px;
  }

  .quota-info {
    font-family: 'Poppins', sans-serif;
    font-size: 11px;
    color: #485066;
    margin-top: 8px;
  }

  .quota-header.range-header {
    .range-col {
      flex: 1;
    }

    .quota-count-label {
      width: 140px;
    }
  }

  .children-section-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0 4px;
  }

  .children-section-label {
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    font-size: 13px;
    color: #0a1b39;
  }

  .children-unit-select {
    .ant-select-selector {
      font-family: 'Poppins', sans-serif !important;
      font-size: 11px !important;
      height: 26px !important;
      border-radius: 4px !important;
      min-width: 70px;
    }

    .ant-select-selection-item {
      line-height: 24px !important;
      font-size: 11px !important;
    }
  }

  .children-entries .entry-input {
    width: 72px !important;
    height: 32px !important;
    border-radius: 6px !important;

    .ant-input-number-input {
      height: 32px !important;
      font-size: 12px !important;
      text-align: right;
    }
  }

  .add-band-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: #3d2969;
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px 0;
    margin-bottom: 8px;
    transition: all 0.15s ease;

    &:hover {
      color: #5a3d9e;
    }

    .anticon {
      font-size: 11px;
    }
  }
`;

export const QuotaChildrenBandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(10, 27, 57, 0.05);
  ${props => props.$hasError && 'background: rgba(255, 77, 79, 0.05); padding-left: 6px; padding-right: 6px; margin: 0 -6px; border-radius: 4px;'}

  &:last-child {
    border-bottom: none;
  }

  .band-age-section {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 140px;
    flex-shrink: 0;
  }

  .band-gender-section {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
  }

  .band-count-section {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    width: 100px;
    justify-content: flex-end;
  }

  .age-input {
    width: 56px !important;
    height: 32px !important;
    border-radius: 6px !important;

    .ant-input-number-input {
      text-align: center;
      font-family: 'Poppins', sans-serif;
      font-size: 12px;
      height: 32px !important;
      padding: 0 4px;
    }
  }

  .range-separator {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    color: #485066;
    flex-shrink: 0;
  }

  .gender-radio {
    display: flex;
    align-items: center;

    .ant-radio-wrapper {
      font-family: 'Poppins', sans-serif;
      font-size: 12px;
      margin-right: 12px;
      color: #0a1b39;
      display: inline-flex !important;
      align-items: center !important;

      &:last-child {
        margin-right: 0;
      }

      .ant-radio {
        top: 0 !important;
      }
    }

    .ant-radio-inner {
      width: 14px !important;
      height: 14px !important;
    }

    .ant-radio-checked .ant-radio-inner {
      border-color: #3d2969 !important;
      background-color: #3d2969 !important;
    }

    .ant-radio-inner::after {
      width: 6px !important;
      height: 6px !important;
      margin-block-start: -3px !important;
      margin-inline-start: -3px !important;
      background-color: #ffffff !important;
    }
  }

  .count-input {
    width: 72px !important;
    height: 32px !important;
    border-radius: 6px !important;

    .ant-input-number-input {
      text-align: right;
      font-family: 'Poppins', sans-serif;
      font-size: 12px;
      height: 32px !important;
    }
  }

  .remove-band-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: none;
    background: none;
    color: #ff4d4f;
    cursor: pointer;
    border-radius: 4px;
    font-size: 11px;
    transition: all 0.15s ease;

    &:hover {
      background: rgba(255, 77, 79, 0.1);
    }
  }
`;

export const QuotaEntryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid rgba(10, 27, 57, 0.05);

  &:last-child {
    border-bottom: none;
  }

  .entry-label {
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    color: #0a1b39;
    flex: 1;
    padding-right: 16px;
  }

  .entry-input {
    width: 100px !important;

    .ant-input-number-input {
      text-align: right;
      font-family: 'Poppins', sans-serif;
      font-size: 13px;
    }
  }
`;

export const QuotaRangeBandRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(10, 27, 57, 0.05);
  ${props => props.$hasError && 'background: rgba(255, 77, 79, 0.05); padding-left: 6px; padding-right: 6px; margin: 0 -6px; border-radius: 4px;'}

  &:last-child {
    border-bottom: none;
  }

  .range-inputs {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
  }

  .range-input {
    width: 100px !important;
    height: 36px !important;
    border-radius: 6px !important;

    .ant-input-number-input {
      text-align: center;
      font-family: 'Poppins', sans-serif;
      font-size: 13px;
      height: 36px !important;
    }
  }

  .range-separator {
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    color: #485066;
  }

  .count-and-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .count-input {
    width: 100px !important;
    height: 36px !important;
    border-radius: 6px !important;

    .ant-input-number-input {
      text-align: right;
      font-family: 'Poppins', sans-serif;
      font-size: 13px;
      height: 36px !important;
    }
  }

  .remove-band-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    background: none;
    color: #ff4d4f;
    cursor: pointer;
    border-radius: 4px;
    font-size: 12px;
    transition: all 0.15s ease;

    &:hover {
      background: rgba(255, 77, 79, 0.1);
    }
  }
`;

export const QuotaTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-top: 1px solid rgba(10, 27, 57, 0.1);
  margin-top: 4px;

  .total-label {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: #0a1b39;
  }

  .total-value {
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: ${props => props.$isError ? '#cf1322' : '#3d2969'};

    .total-max {
      font-weight: 400;
      color: #485066;
    }
  }
`;

export const QuotaActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(10, 27, 57, 0.1);

  .action-btn {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: #3d2969;
    cursor: pointer;
    background: none;
    border: 1px solid rgba(61, 41, 105, 0.2);
    padding: 6px 14px;
    border-radius: 6px;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(61, 41, 105, 0.05);
      border-color: #3d2969;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &.clear-btn {
      color: #485066;
      border-color: rgba(10, 27, 57, 0.15);

      &:hover {
        background: rgba(10, 27, 57, 0.03);
        border-color: rgba(10, 27, 57, 0.3);
      }
    }
  }
`;

// Nested Quota Section
export const NestedQuotaSection = styled.div`
  margin-top: 24px;
  border: 1px solid rgba(10, 27, 57, 0.1);
  border-radius: 10px;
  background: #ffffff;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  .nq-title {
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    font-size: 14px;
    color: #0a1b39;
  }

  .nq-fields {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  .nq-field-tag {
    font-family: 'Poppins', sans-serif;
    font-size: 11px;
    font-weight: 500;
    color: #3d2969;
    background: rgba(61, 41, 105, 0.08);
    padding: 3px 10px;
    border-radius: 12px;
  }

  .nq-summary {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    color: #485066;
    margin-left: auto;
  }

  .nq-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .nq-action-icon {
    font-size: 13px;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: all 0.15s ease;

    &.edit {
      color: #3d2969;
      &:hover { background: rgba(61, 41, 105, 0.08); }
    }

    &.delete {
      color: #ff4d4f;
      &:hover { background: rgba(255, 77, 79, 0.08); }
    }
  }

  .nq-add-btn {
    margin-left: auto;
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: #3d2969;
    background: none;
    border: 1px dashed rgba(61, 41, 105, 0.3);
    padding: 6px 14px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;

    &:hover:not(:disabled) {
      background: rgba(61, 41, 105, 0.05);
      border-color: #3d2969;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
`;

export const NestedQuotaModalContent = styled.div`
  /* Field selection (top) */
  .field-selection {
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid rgba(10, 27, 57, 0.08);
  }

  .field-selection-label {
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: #0a1b39;
    margin-bottom: 10px;
  }

  .field-checkbox-list {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 20px;
  }

  .field-checkbox-item {
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    color: #0a1b39;
    display: inline-flex !important;
    align-items: center !important;

    .ant-checkbox {
      top: 0 !important;
    }

    .ant-checkbox + span {
      display: inline-flex;
      align-items: center;
    }

    .ant-checkbox-checked .ant-checkbox-inner {
      background-color: #3d2969;
      border-color: #3d2969;
    }

    .ant-checkbox:hover .ant-checkbox-inner {
      border-color: #3d2969;
    }

    .field-option-count {
      font-size: 11px;
      color: #485066;
      margin-left: 2px;
    }
  }

  .cell-count-warning {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    color: #fa8c16;
    background: rgba(250, 140, 22, 0.08);
    padding: 8px 12px;
    border-radius: 6px;
    margin-top: 10px;
  }

  .no-cells-hint {
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    color: #485066;
    text-align: center;
    padding: 24px 16px;
    background: #f8f7fa;
    border-radius: 8px;
    border: 1px dashed rgba(10, 27, 57, 0.1);
  }

  .no-fields-hint {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    color: #fa8c16;
    background: rgba(250, 140, 22, 0.08);
    padding: 8px 12px;
    border-radius: 6px;
    margin-top: 10px;
  }

  /* Cell table (bottom) */
  .cell-table-wrapper {
    max-height: 350px;
    overflow-y: auto;
    border: 1px solid rgba(10, 27, 57, 0.1);
    border-radius: 8px;
  }

  .cell-table {
    width: 100%;
    border-collapse: collapse;
    font-family: 'Poppins', sans-serif;
    font-size: 12px;

    thead {
      position: sticky;
      top: 0;
      z-index: 1;

      th {
        background: #f8f7fa;
        color: #485066;
        font-weight: 600;
        font-size: 11px;
        text-transform: uppercase;
        padding: 8px 10px;
        text-align: left;
        border-bottom: 1px solid rgba(10, 27, 57, 0.1);

        &.count-col {
          width: 100px;
          text-align: center;
        }

        &.check-col {
          width: 36px;
          text-align: center;
          padding: 8px 4px;
          vertical-align: middle;

          .ant-checkbox-wrapper {
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }

          .ant-checkbox {
            top: 0 !important;
          }

          .ant-checkbox-inner {
            width: 16px;
            height: 16px;
            border-radius: 3px;
            border: 1.5px solid rgba(10, 27, 57, 0.25);
          }

          .ant-checkbox-checked .ant-checkbox-inner {
            background-color: #3d2969;
            border-color: #3d2969;
          }

          .ant-checkbox-indeterminate .ant-checkbox-inner::after {
            background-color: #3d2969;
          }

          .ant-checkbox:hover .ant-checkbox-inner {
            border-color: #3d2969;
          }

          .ant-checkbox-checked::after {
            border-color: #3d2969;
          }
        }

        &.action-col {
          width: 36px;
          text-align: center;
          padding: 8px 4px;
        }
      }
    }

    tbody {
      tr {
        border-bottom: 1px solid rgba(10, 27, 57, 0.05);

        &:last-child { border-bottom: none; }
        &:hover { background: rgba(61, 41, 105, 0.02); }
      }

      tr.combined-group-row {
        background: rgba(61, 41, 105, 0.06);
        &:hover { background: rgba(61, 41, 105, 0.09); }

        td { font-weight: 600; color: #3d2969; }

        .combined-label {
          font-weight: 600;
          color: #3d2969;
        }

        .combined-label-input {
          font-weight: 600;
          color: #3d2969;
          font-family: 'Poppins', sans-serif;
          font-size: 12px;
          padding: 0 4px;
          background: transparent;

          &:hover {
            background: rgba(61, 41, 105, 0.04);
          }

          &:focus {
            background: #ffffff;
            box-shadow: 0 0 0 1px #3d2969;
            border-radius: 4px;
          }

          .ant-input {
            font-weight: 600;
            color: #3d2969;
            height: auto !important;
            border: none !important;
            padding: 2px 0 !important;
          }
        }
      }

      td {
        padding: 6px 10px;
        color: #0a1b39;

        &.count-col {
          text-align: center;

          .ant-input-number {
            width: 80px;
          }
        }

        &.check-col {
          text-align: center;
          padding: 6px 4px;
          vertical-align: middle;

          .ant-checkbox-wrapper {
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }

          .ant-checkbox {
            top: 0 !important;
          }

          .ant-checkbox-inner {
            width: 16px;
            height: 16px;
            border-radius: 3px;
            border: 1.5px solid rgba(10, 27, 57, 0.25);
          }

          .ant-checkbox-checked .ant-checkbox-inner {
            background-color: #3d2969;
            border-color: #3d2969;
          }

          .ant-checkbox:hover .ant-checkbox-inner {
            border-color: #3d2969;
          }

          .ant-checkbox-checked::after {
            border-color: #3d2969;
          }
        }

        &.action-col {
          text-align: center;
          padding: 6px 4px;
        }
      }
    }

    .cell-delete-icon {
      color: #ff4d4f;
      cursor: pointer;
      font-size: 13px;
      padding: 2px 4px;
      border-radius: 4px;

      &:hover {
        background: rgba(255, 77, 79, 0.08);
      }
    }
  }

  .cell-total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 12px;
    padding: 10px 12px;
    background: #f8f7fa;
    border-radius: 6px;
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    font-weight: 600;

    &.error { color: #ff4d4f; background: rgba(255, 77, 79, 0.05); }
    &.warning { color: #fa8c16; background: rgba(250, 140, 22, 0.05); }
  }

  .quota-info {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    color: #fa8c16;
    margin-top: 8px;
  }

  .quota-error {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    color: #ff4d4f;
    margin-top: 8px;
  }

  .qbp-warning {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    color: #fa8c16;
    background: rgba(250, 140, 22, 0.08);
    padding: 8px 12px;
    border-radius: 6px;
    margin-top: 12px;
  }
`;

export const NestedQuotaActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;

  .action-btn {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: #3d2969;
    cursor: pointer;
    background: none;
    border: 1px solid rgba(61, 41, 105, 0.2);
    padding: 6px 14px;
    border-radius: 6px;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(61, 41, 105, 0.05);
      border-color: #3d2969;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &.clear-btn {
      color: #485066;
      border-color: rgba(10, 27, 57, 0.15);

      &:hover {
        background: rgba(10, 27, 57, 0.03);
        border-color: rgba(10, 27, 57, 0.3);
      }
    }

    &.combine-btn {
      color: #ffffff;
      background: #3d2969;
      border-color: #3d2969;

      &:hover {
        background: #5a3d8f;
        border-color: #5a3d8f;
      }

      &:disabled {
        background: rgba(61, 41, 105, 0.25);
        border-color: transparent;
        color: rgba(255, 255, 255, 0.6);
        cursor: not-allowed;

        &:hover {
          background: rgba(61, 41, 105, 0.25);
          border-color: transparent;
        }
      }
    }
  }
`;

// Zipcode CSV Upload Area - input-style upload box
export const ZipcodeUploadArea = styled.div`
  .zipcode-input-box {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 48px;
    padding: 0 14px;
    border: 1px solid rgba(10, 27, 57, 0.1);
    border-radius: 10px;
    background: #ffffff;
    cursor: pointer;
    transition: border-color 0.2s ease;

    &:hover {
      border-color: rgba(10, 27, 57, 0.2);
    }

    &.has-file {
      cursor: default;
      background: rgba(61, 41, 105, 0.03);
      border-color: rgba(61, 41, 105, 0.15);
    }

    .upload-icon {
      color: rgba(10, 27, 57, 0.35);
      font-size: 16px;
      flex-shrink: 0;
    }

    .upload-placeholder {
      font-family: 'Poppins', sans-serif;
      font-size: 14px;
      color: rgba(10, 27, 57, 0.5);
      flex: 1;
    }

    .file-icon {
      color: #3d2969;
      font-size: 16px;
      flex-shrink: 0;
    }

    .file-name {
      font-family: 'Poppins', sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: #0a1b39;
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .file-count {
      font-family: 'Poppins', sans-serif;
      font-size: 12px;
      color: #485066;
      flex-shrink: 0;
    }

    .view-btn {
      font-family: 'Poppins', sans-serif;
      font-size: 12px;
      font-weight: 500;
      color: #3d2969;
      background: none;
      border: none;
      cursor: pointer;
      padding: 2px 6px;
      border-radius: 4px;
      flex-shrink: 0;
      transition: background 0.15s ease;

      &:hover {
        background: rgba(61, 41, 105, 0.1);
      }
    }

    .clear-btn {
      color: rgba(10, 27, 57, 0.35);
      font-size: 12px;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      flex-shrink: 0;
      transition: all 0.15s ease;

      &:hover {
        color: #ff4d4f;
        background: rgba(255, 77, 79, 0.08);
      }
    }
  }

  .zipcode-preview {
    max-height: 400px;
    overflow-y: auto;

    .preview-item {
      padding: 8px 0;
      border-bottom: 1px solid rgba(10, 27, 57, 0.06);
      font-family: 'Poppins', sans-serif;
      font-size: 13px;
      color: #0a1b39;

      &:last-child {
        border-bottom: none;
      }
    }
  }

  .download-tpl-btn {
    font-family: 'Poppins', sans-serif;
    font-size: 11px;
    font-weight: 500;
    color: #3d2969;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      color: #5a3d9e;
    }
  }
`;

export const FeasibilitySection = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-start;
`;

export const FeasibilityResultCard = styled.div`
  margin-top: 16px;
  padding: 20px;
  border: 1px solid rgba(10, 27, 57, 0.1);
  border-radius: 10px;
  background: #f8f7fa;

  &.error {
    background: #fff2f0;
    border-color: #ffccc7;
  }

  .result-title {
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    font-size: 14px;
    color: #0a1b39;
    margin-bottom: 16px;
  }

  .result-error {
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    color: #ff4d4f;
  }

  .result-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;

    @media (max-width: 992px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 576px) {
      grid-template-columns: 1fr;
    }
  }

  .result-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 16px;
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid rgba(10, 27, 57, 0.05);
  }

  .result-label {
    font-family: 'Poppins', sans-serif;
    font-size: 11px;
    font-weight: 500;
    color: #485066;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .result-value {
    font-family: 'Poppins', sans-serif;
    font-size: 18px;
    font-weight: 600;
    color: #0a1b39;

    &.highlight {
      color: #3d2969;
    }
  }

  .use-cpi-link {
    margin-top: 4px;
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: #3d2969;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      color: #5b3fa0;
    }

    .anticon {
      font-size: 10px;
      margin-right: 3px;
    }
  }

  .difficult-quotas {
    margin-top: 16px;
    padding: 12px 16px;
    background: #fffbe6;
    border: 1px solid #ffe58f;
    border-radius: 8px;
  }

  .warning-title {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: #d48806;
    margin-bottom: 8px;
  }

  .warning-item {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    color: #614700;
    padding: 2px 0;
  }

  .unused-info {
    margin-top: 12px;
    padding: 10px 16px;
    background: #e6f4ff;
    border: 1px solid #91caff;
    border-radius: 8px;
  }

  .info-title {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: #0958d9;
    margin-bottom: 4px;
  }

  .info-text {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    color: #003eb3;
  }
`;

// Field Evenly Over Time section — card-style container
export const FeotSection = styled.div`
  border: 1px solid rgba(10, 27, 57, 0.1);
  border-radius: 10px;
  background: #ffffff;
  padding: 14px 20px;
  margin-bottom: 16px;

  .feot-line {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .feot-label {
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    font-size: 13px;
    color: #0a1b39;
  }

  .feot-calc {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    color: #485066;
    margin-left: auto;

    strong {
      font-weight: 600;
      color: #0a1b39;
    }
  }

  .feot-custom-line {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid rgba(10, 27, 57, 0.06);
  }

  .feot-field-label {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: #485066;
  }

  .feot-toggle-label {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    color: #485066;
  }

  .feot-divider {
    width: 1px;
    height: 16px;
    background: rgba(10, 27, 57, 0.1);
    flex-shrink: 0;
  }

  .feot-field-hint {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    color: #485066;
  }

  /* Radio group styling matching InlineRadioGroup */
  .ant-radio-wrapper {
    font-size: 12px;
    font-family: 'Poppins', sans-serif;
    color: #0a1b39;

    .ant-radio {
      top: 0;
    }

    .ant-radio-inner {
      width: 14px;
      height: 14px;
      border: 1px solid rgba(31, 36, 48, 0.2);
    }

    .ant-radio-checked .ant-radio-inner {
      border-color: #3d2969 !important;
      background-color: #3d2969 !important;
    }

    .ant-radio:hover .ant-radio-inner {
      border-color: #3d2969 !important;
    }

    .ant-radio-inner::after {
      background-color: white !important;
      width: 6px;
      height: 6px;
      margin-block-start: -3px;
      margin-inline-start: -3px;
    }
  }

  /* Override FormContent global .ant-input-number styles for small interval input */
  .feot-interval-input {
    width: 70px;

    &.ant-input-number {
      border: 1px solid rgba(10, 27, 57, 0.1) !important;
      border-radius: 8px !important;
      height: 32px !important;
    }

    &.ant-input-number-focused {
      border-color: #3d2969 !important;
      box-shadow: 0 0 0 2px rgba(61, 41, 105, 0.1) !important;
    }

    .ant-input-number-input {
      font-family: 'Poppins', sans-serif !important;
      font-size: 12px !important;
      color: #0a1b39 !important;
      height: 30px !important;
      padding: 0 8px !important;
      text-align: center;
    }
  }
`;
